'use client'
import { listQuestions } from "@/app/api";
import { ArrowDown, ArrowUp, ChevronDown, Pen, Trash } from "@/assets/icons";
import { Table, TableRow, TableHeader, TableCell, SmartLink, Pagination, ColorButton, MoreButton } from "@/components/ui";
import { CommentSearchParams, StatusFilterParams, QuestionsResponse } from "@/types";
import React, { useEffect, useState } from "react";

export function ManageQuestions() {
    const [questionsData, setQuestionsData] = useState<QuestionsResponse>();
    const [filters, setFilters] = useState<CommentSearchParams>({
        page: 1,
        perPage: 10,
        order: 'desc',
        orderBy: 'createdAt',
        status: 'pending',
    });

    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                const data = await listQuestions(filters)
                setQuestionsData(data)
            } catch (err) {
                console.log("Erro ao carregar perguntas: ", err);
            }
        }

        fetchQuestions()
    }, [filters])

    const handleStatusChange = (status: StatusFilterParams) => {
        setFilters({ ...filters, status, page: 1 })
    }

    const handlePageChange = (page: number) => {
        setFilters({ ...filters, page });
    };

    const setOrder = (newOrder: 'asc' | 'desc') => {
        setFilters(prev => ({ ...prev, order: newOrder, page: 1 }));
    };

    const questions = questionsData?.data || []
    const [expandedQuestions, setExpandedQuestions] = useState<Record<string, boolean>>({});
    const [showAnswer, setShowAnswer] = useState<Record<string, boolean>>({});

    const toggleExpand = (questionId: string) => {
        setExpandedQuestions(prev => ({
            ...prev,
            [questionId]: !prev[questionId]
        }));
    };
    const toggleShowAnswer = (questionId: string) => {
        setShowAnswer(prev => ({
            ...prev,
            [questionId]: !prev[questionId]
        }));
    };
    const truncateText = (text: string, length: number) => {
        return text.length > length ? text.substring(0, length) + '...' : text;
    };

    return (
        <div className="pt-8">
            <div className="flex justify-start">
                <div className="flex flex-wrap gap-4 justify-center items-center">

                    <ColorButton disabled={filters.status === 'pending'}
                        onClick={() => { handleStatusChange('pending') }}>
                        Pendentes
                    </ColorButton>
                    <ColorButton disabled={filters.status === 'approved'}
                        onClick={() => { handleStatusChange('approved') }}>
                        Aprovadas
                    </ColorButton>
                    <ColorButton disabled={filters.status === 'rejected'}
                        onClick={() => { handleStatusChange('rejected') }}>
                        Reprovadas
                    </ColorButton>
                    <ColorButton disabled={filters.status === 'flagged'}
                        onClick={() => { handleStatusChange('flagged') }}>
                        Sinalizadas
                    </ColorButton>

                </div>
            </div>



            <div className="p-8">
                <div className="hidden md:flex">
                    <Table>
                        <thead>
                            <TableRow>
                                <TableHeader>Sequência</TableHeader>
                                <TableHeader>Conteúdo</TableHeader>
                                <TableHeader>Autor</TableHeader>
                                <TableHeader>Email do autor</TableHeader>
                                <TableHeader>Status</TableHeader>
                                <TableHeader>Data do envio</TableHeader>
                                <TableHeader>
                                    <button
                                        onClick={() => setOrder(filters.order === 'asc' ? 'desc' : 'asc')}
                                    >
                                        {filters.order === 'asc' ? <ArrowUp className="size-4" /> : <ArrowDown className="size-4" />}
                                    </button></TableHeader>
                            </TableRow>
                        </thead>
                        <tbody>
                            {questions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center">
                                        Não existem perguntas desse status.
                                    </TableCell>
                                </TableRow>
                            ) : (questions.map((question) => (
                                <React.Fragment key={question.id}>
                                    <TableRow>
                                        <TableCell>{question.sequence}</TableCell>
                                        <TableCell>
                                            {expandedQuestions[question.id]
                                                ? question.content
                                                : truncateText(question.content, 100)}
                                            <button onClick={() => toggleExpand(question.id)}>
                                                <ChevronDown className="size-4" />
                                            </button>
                                        </TableCell>

                                        <TableCell>
                                            {question.user?.name ? 'Usuário' : 'Visitante'} - {question.user?.name || question.visitor?.name}
                                        </TableCell>

                                        <TableCell>
                                            {question.user?.email || question.visitor?.email || 'Não informado'}
                                        </TableCell>

                                        <TableCell>
                                            {question.status === 'PENDING' ? 'Pendente'
                                                : question.status === 'APPROVED' ? "Aprovada"
                                                    : question.status === 'REJECTED' ? 'Rejeitada'
                                                        : question.status === 'FLAGGED' ? 'Sinalizada'
                                                            : 'Pendente'
                                            }
                                        </TableCell>

                                        <TableCell>{new Date(question.createdAt).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            <MoreButton onClick={() => toggleShowAnswer(question.id)} />
                                        </TableCell>
                                    </TableRow>
                                    {showAnswer[question.id] && (
                                        <TableRow>
                                            <TableHeader>Resposta</TableHeader>
                                            <TableCell colSpan={4}>{question.answer}</TableCell>
                                            <TableCell colSpan={2} className="flex gap-10">
                                                <SmartLink type={"internal"} route={"adminCommentsQuestionsIdUpdate"}
                                                    params={[`${question.id}`]}>
                                                    <Pen />
                                                </SmartLink>
                                                <SmartLink type={"internal"} route={"adminCommentsQuestionsIdDelete"}
                                                    params={[`${question.id}`]}>
                                                    <Trash />
                                                </SmartLink>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            )))
                            }
                        </tbody>
                    </Table>
                </div>

                <div className="flex md:hidden">
                    <Table>
                        <tbody>
                            {questions.length === 0 ? (
                                <TableRow>
                                    <TableCell className="text-center">
                                        Não existem perguntas desse status.
                                    </TableCell>
                                </TableRow>
                            ) : (questions.map((question) => (
                                <TableRow key={question.id} >
                                    <TableHeader>Conteúdo</TableHeader>
                                    <TableCell>{question.content}</TableCell>

                                    <TableHeader>Autor</TableHeader>
                                    <TableCell>
                                        {question.user?.name ? 'Usuário' : 'Visitante'} - {question.user?.name || question.visitor?.name}
                                    </TableCell>

                                    <TableHeader>Email do autor</TableHeader>
                                    <TableCell>{question.user?.email || question.visitor?.email || 'Não informado'}</TableCell>

                                    <TableHeader>Status</TableHeader>
                                    <TableCell>
                                        {question.status === 'PENDING' ? 'Pendente'
                                            : question.status === 'APPROVED' ? "Aprovada"
                                                : question.status === 'REJECTED' ? 'Rejeitada'
                                                    : question.status === 'FLAGGED' ? 'Sinalizada'
                                                        : 'Pendente'
                                        }
                                    </TableCell>

                                    <TableHeader colSpan={2}>Data do envio</TableHeader>
                                    <TableCell>
                                        {new Date(question.createdAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableHeader>Resposta</TableHeader>
                                    <TableCell>{question.answer}</TableCell>
                                    <TableCell className="flex gap-10">
                                        <SmartLink type={"internal"} route={"adminCommentsQuestionsIdUpdate"}
                                            params={[`${question.id}`]}>
                                            <Pen />
                                        </SmartLink>
                                        <SmartLink type={"internal"} route={"adminCommentsQuestionsIdDelete"}
                                            params={[`${question.id}`]}>
                                            <Trash />
                                        </SmartLink>
                                    </TableCell>
                                    <TableCell className="border-t-2 min-w-40 border-ochre_accent" />
                                </TableRow>
                            ))
                            )
                            }
                        </tbody>
                    </Table>
                </div>
            </div>

            <div>
                {questionsData?.pagination && (
                    <Pagination
                        currentPage={questionsData.pagination.page}
                        totalPages={Math.ceil(questionsData.pagination.total / questionsData.pagination.perPage)}
                        onPageChange={handlePageChange}
                    />)}
            </div>
        </div>
    )
}
